import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useEffect, useState } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import { buildThemeStyle } from "./buildThemeStyle";
import "./index.css";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import { useLiveData, useLiveDataForMatch } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { useMatchData } from "@/hooks/useMatchData";
import hotIcon from "@/assets/images/hot.svg";

const HOT_LEAGUE_ID = "1572";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const COLLAPSED_COUNT = 10;


const ResultRow = ({ match, isLast }) => {
  const { getScore } = useLiveDataForMatch(match.id);
  const liveScore = getScore();
  const { currentHomeScore, currentAwayScore, isHomeWin, isAwayWin } = useMatchData(liveScore, match);
  const leagueName = match.league?.nameVN || match.league?.name || "";

  const isHotLeague = String(match.league?.id) === HOT_LEAGUE_ID;

  return (
    <div className="kbv-kqv2-row relative px-3 py-2">
      <div className="mb-2 flex items-center gap-2 min-w-0">
        {match.league?.logo && (
          <img
            src={match.league.logo}
            alt={leagueName}
            className="w-5 h-5 object-contain shrink-0"
            loading="lazy"
          />
        )}
        <div className="flex items-center gap-1 min-w-0">
          <span
            className={`text-sm font-bold leading-5 truncate ${isHotLeague ? "text-[var(--kbv-kqv2-hot-league,#FFDF58)]" : "text-[var(--theme-subtext,var(--theme-text-muted))]"}`}
            title={leagueName}
          >
            {leagueName}
          </span>
          {isHotLeague ? (
            <img src={hotIcon} alt="hot" className="h-4 w-auto shrink-0" loading="lazy" />
          ) : null}
        </div>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_28px] gap-2">
        <div className="min-w-0">
          <div className="flex items-start gap-2 min-w-0">
            {match.homeTeam?.logo && (
              <img
                src={match.homeTeam.logo}
                alt={match.homeName}
                className="w-5 h-5 rounded-full object-cover shrink-0 mt-0.5"
                loading="lazy"
              />
            )}
            <span
              className={`text-sm leading-5 truncate ${isHomeWin ? "font-bold text-[var(--theme-text)]" : "font-semibold text-[var(--theme-text)]"}`}
              title={match.homeName}
            >
              {match.homeTeam?.nameVi || match.homeName}
            </span>
          </div>
          <div className="flex items-start gap-2 min-w-0 mt-1">
            {match.awayTeam?.logo && (
              <img
                src={match.awayTeam.logo}
                alt={match.awayName}
                className="w-5 h-5 rounded-full object-cover shrink-0 mt-0.5"
                loading="lazy"
              />
            )}
            <span
              className={`text-sm leading-5 truncate ${isAwayWin ? "font-bold text-[var(--theme-text)]" : "font-semibold text-[var(--theme-text)]"}`}
              title={match.awayName}
            >
              {match.awayTeam?.nameVi || match.awayName}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end justify-center">
          <span className="kbv-kqv2-score text-sm font-bold tabular-nums text-[var(--kbv-kqv2-score,#ef4444)] leading-5">
            {currentHomeScore}
          </span>
          <span className="kbv-kqv2-score text-sm font-bold tabular-nums text-[var(--kbv-kqv2-score,#ef4444)] leading-5 mt-1">
            {currentAwayScore}
          </span>
        </div>
      </div>
      {!isLast ? <span className="absolute left-3 right-3 bottom-0 h-px bg-[var(--theme-border)]" /> : null}
    </div>
  );
};

const BangKeoKQV2 = ({ type = "all" }) => {
  const compConfig = useCompConfig();
  const { hasDataConfig } = compConfig;
  const themeStyle = buildThemeStyle(compConfig, hasDataConfig);

  const todayDate = dayjs().tz(TZ);
  const maxDate = todayDate.format("YYYY-MM-DD");
  const minDate = todayDate.subtract(7, "day").format("YYYY-MM-DD");
  const { clearAllData } = useLiveData();
  const [selectedDate, setSelectedDate] = useState(() => maxDate);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDateChange = (event) => {
    const nextDate = event.target.value;
    if (!nextDate || nextDate < minDate || nextDate > maxDate) return;
    setSelectedDate(nextDate);
    setIsExpanded(false);
  };

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${selectedDate}`,
      filterOdds: -1,
      cache: false,
      status: type === "ketqua" ? "end" : "",
      page: 1,
      limit: 20,
    }),
    [selectedDate, type],
  );

  const { leaguesWithMatches, loading } = useBangKeoMatches(queryObject, false);

  const matches = useMemo(
    () =>
      leaguesWithMatches.flatMap((leagueItem) =>
        (leagueItem.matches || []).map((match) => ({
          ...match,
          league: leagueItem.league || match.league,
        })),
      ),
    [leaguesWithMatches],
  );
  const visibleMatches = useMemo(
    () => (isExpanded ? matches : matches.slice(0, COLLAPSED_COUNT)),
    [isExpanded, matches],
  );
  const canToggleExpand = matches.length > COLLAPSED_COUNT;

  const matchIds = useMemo(() => matches.map((match) => match.id), [matches]);

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  return (
    <div
      className="kbv-kqv2 w-full max-w-full rounded-xl border border-[var(--theme-border)] bg-[var(--theme-foreground)] overflow-hidden"
      style={themeStyle}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-3 border-b border-[var(--theme-border)]">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="kbv-kqv2-accent w-1 h-5 shrink-0 rounded-full bg-[var(--theme-primary)]" aria-hidden="true" />
          <h3 className="text-base font-bold uppercase tracking-tight truncate text-[var(--theme-text)]">Kết quả</h3>
        </div>
        <input
          type="date"
          value={selectedDate}
          min={minDate}
          max={maxDate}
          onChange={handleDateChange}
          className="kbv-kqv2-date-input shrink-0 rounded-md border border-[var(--theme-border)] bg-[var(--theme-bg)] px-2 py-1 text-xs font-medium text-[var(--theme-text)] outline-none transition-colors focus:border-[var(--theme-primary)] [color-scheme:dark]"
          aria-label="Chọn ngày xem kết quả"
        />
      </div>
      {loading && <LoadingInner />}
      {!loading && (
        <div className="w-full">
          {matches.length === 0 ? (
            <div className="p-3">
              <NoData message="Không có trận đấu nào" />
            </div>
          ) : (
            <>
              {visibleMatches.map((match, index) => (
                <ResultRow
                  key={match.id}
                  match={match}
                  isLast={index === visibleMatches.length - 1 && !canToggleExpand}
                />
              ))}
              {canToggleExpand && (
                <div className="px-3 py-2.5 border-t border-[var(--theme-border)] bg-[var(--theme-foreground)]">
                  <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="kbv-kqv2-expand-btn w-full text-sm font-medium text-[var(--theme-subtext,var(--theme-text-muted))] hover:text-[var(--theme-text)] transition-colors"
                  >
                    {isExpanded ? "Thu gọn" : "Mở rộng"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BangKeoKQV2;
